import type { Account } from 'generated/gql';
import { AccountEntitlement } from 'generated/gql';
import { createTypedContextProvider } from 'providers/createTypedContextProvider';
import { useMemo } from 'react';

type AccountContext = {
  account: Account | undefined;
  refetchAccount: () => void;
};

export const [useAccountContext, AccountContextProvider] =
  createTypedContextProvider<AccountContext>();

export const useAccount = () => useAccountContext()?.account;
export const useRefetchAccount = () => useAccountContext()?.refetchAccount;

export const useEntitlements = (
  neededEntitlements?: AccountEntitlement[],
): [AccountEntitlement[], boolean] => {
  const account = useAccount();

  const entitlements = useMemo(() => account?.entitlements || [], [account?.entitlements]);
  const hasEntitlements =
    entitlements.filter((entitlement) => neededEntitlements?.includes(entitlement)).length !== 0;

  return [entitlements, hasEntitlements];
};

export const useHasLicenseAgreement = () => {
  const account = useAccount();
  return account?.hasLicenseAgreement;
};

/**
 * The Groups page and the group pages only need GROUP_MANAGEMENT, which follows from the user's own
 * `groupAdmin` flag. Evidend admins bypass it.
 */
export const useCanViewGroups = () => {
  const [, hasSuperAdmin] = useEntitlements([AccountEntitlement.SuperAdmin]);
  const [, hasGroupManagement] = useEntitlements([AccountEntitlement.GroupManagement]);

  return hasSuperAdmin || hasGroupManagement;
};

/**
 * Creating groups, and users in them, needs GROUP_MANAGEMENT (from the user's own `groupAdmin`
 * flag) *and* USER_MANAGEMENT (from the user's own `userAdmin` flag), within a notary office.
 * Evidend admins bypass it.
 */
export const useCanManageGroups = () => {
  const account = useAccount();
  // useEntitlements is OR across the array, so each entitlement needs its own call.
  const [, hasSuperAdmin] = useEntitlements([AccountEntitlement.SuperAdmin]);
  const [, hasUserManagement] = useEntitlements([AccountEntitlement.UserManagement]);
  const [, hasGroupManagement] = useEntitlements([AccountEntitlement.GroupManagement]);

  return hasSuperAdmin || (!!account?.notary && hasUserManagement && hasGroupManagement);
};

/**
 * Every KvK-backed call (kvkUpdates, kvkLookup, addKvkNumberToLegalEntity) funnels through
 * KvkService.getCompany, which is secured on PARTY_CREATE — despite the server's error text
 * reading "You have no permission to perform party update". Register-level MANAGE is not enough.
 */
export const useCanFetchFromKvk = () => {
  const [, hasPartyCreate] = useEntitlements([AccountEntitlement.PartyCreate]);
  return hasPartyCreate;
};
